import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { inputCls, labelCls } from "@/lib/modal";
import { Loader2, RefreshCw, Send, Users, ShieldCheck, Building2, Share2, Flag, LogOut } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-2xl card-surface p-6" data-testid={testid}>
    <Icon className="h-4 w-4 text-cyan-400 mb-4" />
    <div className="font-display text-3xl font-medium text-white tabular-nums">{value}</div>
    <div className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">{label}</div>
  </div>
);

const Th = ({ children }) => (
  <th className="px-4 py-3 text-left font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500 whitespace-nowrap">
    {children}
  </th>
);

const Td = ({ children, mono = false }) => (
  <td className={`px-4 py-3 text-sm text-zinc-300 whitespace-nowrap ${mono ? "font-mono text-[11px] tracking-[0.06em]" : ""}`}>
    {children}
  </td>
);

export default function Admin() {
  const [key, setKey] = useState(() => localStorage.getItem("synaphy_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("waitlist");
  const [members, setMembers] = useState([]);
  const [validators, setValidators] = useState([]);
  const [pilots, setPilots] = useState([]);
  const [activity, setActivity] = useState([]);
  const [digestBusy, setDigestBusy] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);

  const auth = useCallback(
    () => ({ headers: { "X-Admin-Key": localStorage.getItem("synaphy_admin_key") || "" } }),
    []
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, m, v, p, a] = await Promise.all([
        axios.get(`${API}/admin/overview`, auth()),
        axios.get(`${API}/admin/members`, auth()),
        axios.get(`${API}/admin/validators`, auth()),
        axios.get(`${API}/admin/pilots`, auth()),
        axios.get(`${API}/admin/activity`, auth()),
      ]);
      setOverview(o.data);
      setMembers(m.data.members);
      setValidators(v.data.applications);
      setPilots(p.data.requests);
      setActivity(a.data.activity);
      setAuthed(true);
    } catch {
      setAuthed(false);
      toast.error("Invalid admin key or server error.");
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    if (key) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unlock = (e) => {
    e.preventDefault();
    localStorage.setItem("synaphy_admin_key", key.trim());
    load();
  };

  const lock = () => {
    localStorage.removeItem("synaphy_admin_key");
    setAuthed(false);
    setKey("");
    setOverview(null);
  };

  const sendDigest = async () => {
    setDigestBusy(true);
    try {
      const res = await axios.post(`${API}/admin/weekly-digest`, null, auth());
      toast.success(`Digest sent to ${res.data.sent} members.`);
    } catch {
      toast.error("Digest failed.");
    } finally {
      setDigestBusy(false);
    }
  };

  const syncLegacy = async () => {
    setSyncBusy(true);
    try {
      const res = await axios.post(`${API}/admin/invites/sync-legacy`, null, auth());
      toast.success(res.data.synced ? `Synced ${res.data.synced} stuck invite(s).` : "No stuck invites to sync.");
      load();
    } catch {
      toast.error("Sync failed.");
    } finally {
      setSyncBusy(false);
    }
  };

  const reviewInvite = async (row, action) => {
    try {
      await axios.post(
        `${API}/admin/invites/review`,
        { genesis_id: row.genesis_id, action },
        auth()
      );
      toast.success(`Invite ${action === "approve" ? "approved" : "rejected"}.`);
      load();
    } catch {
      toast.error("Review action failed.");
    }
  };

  return (
    <>
    <Helmet><meta name="robots" content="noindex,nofollow" /><title>Admin Console</title></Helmet>
    <div className="relative min-h-screen bg-[#050505]" data-testid="admin-page">
      <div className="absolute inset-0 bg-grid-fine opacity-30 pointer-events-none" />
      <header className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-4 group" data-testid="admin-logo" aria-label="Synaphy home">
          <img src="/logo-lockup.png" alt="Synaphy - Intelligence for the physical world" className="h-14 w-auto object-contain drop-shadow-[0_0_18px_rgba(90,150,255,0.35)]" />
          <span className="font-mono text-[8px] tracking-[0.3em] text-zinc-500 uppercase border-l border-white/10 pl-4">Admin Console</span>
        </Link>
        {authed && (
          <button
            data-testid="admin-logout-btn"
            onClick={lock}
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 transition-colors hover:text-red-400"
          >
            <LogOut className="h-3.5 w-3.5" /> Lock
          </button>
        )}
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-10 pb-24">
        {!authed ? (
          <motion.form
            onSubmit={unlock}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto mt-16 max-w-md rounded-2xl card-surface p-8 sm:p-10"
            data-testid="admin-gate"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-400 mb-3">
              Restricted
            </div>
            <h1 className="font-display text-3xl font-medium tracking-tight text-white">
              Admin Console
            </h1>
            <p className="mt-3 text-sm text-zinc-500 leading-relaxed">
              Enter the admin key to view waitlist members, verifier applications
              and pilot requests.
            </p>
            <div className="mt-7">
              <label htmlFor="admin-key" className={labelCls}>Admin Key</label>
              <input
                id="admin-key"
                data-testid="admin-key-input"
                type="password"
                required
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="agk_..."
                className={`${inputCls} font-mono`}
              />
            </div>
            <button
              type="submit"
              data-testid="admin-unlock-btn"
              disabled={loading}
              className="mt-6 w-full rounded-full bg-cyan-400 py-3.5 font-display text-sm font-semibold tracking-wider text-black transition-colors hover:bg-cyan-300 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              UNLOCK CONSOLE
            </button>
          </motion.form>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-400 mb-2">
                  Live Network Data
                </div>
                <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-white" data-testid="admin-title">
                  Founding Overview
                </h1>
              </div>
              <div className="flex items-center gap-2.5">
                <button
                  data-testid="admin-refresh-btn"
                  onClick={load}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 font-mono text-[10px] tracking-[0.2em] text-zinc-300 transition-colors hover:border-cyan-400/50 hover:text-cyan-400"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> REFRESH
                </button>
                <button
                  data-testid="admin-digest-btn"
                  onClick={sendDigest}
                  disabled={digestBusy}
                  className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-4 py-2.5 font-mono text-[10px] tracking-[0.2em] text-black transition-colors hover:bg-cyan-300 disabled:opacity-60"
                >
                  {digestBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  SEND WEEKLY DIGEST
                </button>
                <button
                  data-testid="admin-sync-invites-btn"
                  onClick={syncLegacy}
                  disabled={syncBusy}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 font-mono text-[10px] tracking-[0.2em] text-zinc-300 transition-colors hover:border-cyan-400/50 hover:text-cyan-400 disabled:opacity-60"
                >
                  {syncBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                  SYNC STUCK INVITES
                </button>
              </div>
            </div>

            {overview && (
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10" data-testid="admin-overview">
                <StatCard icon={Users} label="Waitlist Members" value={overview.genesis_members} testid="stat-members" />
                <StatCard icon={ShieldCheck} label="Verifier Applications" value={overview.validator_applications} testid="stat-validators" />
                <StatCard icon={Building2} label="Pilot Requests" value={overview.pilot_requests} testid="stat-pilots" />
                <StatCard icon={Share2} label="Verified Invites" value={overview.verified_invites} testid="stat-invites" />
                <StatCard icon={Flag} label="Invites In Review" value={overview.invites_in_review} testid="stat-review" />
              </div>
            )}

            <div className="flex gap-2 mb-6" data-testid="admin-tabs">
              {[
                ["waitlist", `Waitlist (${members.length})`],
                ["activity", `Invites (${activity.length})`],
                ["validators", `Verifiers (${validators.length})`],
                ["pilots", `Pilots (${pilots.length})`],
              ].map(([id, label]) => (
                <button
                  key={id}
                  data-testid={`admin-tab-${id}`}
                  onClick={() => setTab(id)}
                  className={`rounded-full px-5 py-2.5 font-mono text-[10px] tracking-[0.18em] transition-colors ${
                    tab === id
                      ? "bg-cyan-400 text-black"
                      : "border border-white/15 text-zinc-400 hover:border-cyan-400/40 hover:text-cyan-400"
                  }`}
                >
                  {label.toUpperCase()}
                </button>
              ))}
            </div>

            <div className="rounded-2xl card-surface overflow-hidden" data-testid="admin-table-wrap">
              <div className="overflow-x-auto">
                {tab === "waitlist" && (
                  <table className="w-full" data-testid="admin-members-table">
                    <thead className="border-b border-white/[0.07] bg-white/[0.02]">
                      <tr>
                        <Th>#</Th><Th>Name</Th><Th>Email</Th><Th>Founding ID</Th><Th>Mobile</Th><Th>Country</Th><Th>State</Th><Th>Referred By</Th><Th>Invites</Th><Th>Emails</Th><Th>Milestones</Th><Th>Status</Th><Th>Joined</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((m) => (
                        <tr key={m.genesis_id} className="border-b border-white/[0.04] last:border-0 hover:bg-white/[0.02]">
                          <Td mono>#{String(m.position).padStart(4, "0")}</Td>
                          <Td>{m.name}</Td>
                          <Td mono>{m.email}</Td>
                          <Td mono><span className="text-cyan-400">{m.genesis_id}</span></Td>
                          <Td mono>{m.mobile || "-"}</Td>
                          <Td>{m.country || "-"}</Td>
                          <Td>{m.state || "-"}</Td>
                          <Td mono>{m.referral ? <span className="text-cyan-400/80">{m.referral}</span> : "-"}</Td>
                          <Td mono>{m.referral_verified ?? m.referral_count ?? 0}</Td>
                          <Td>
                            {m.unsubscribed ? (
                              <span className="rounded-full border border-red-400/40 px-2.5 py-0.5 font-mono text-[8px] tracking-[0.15em] text-red-300">OPTED OUT</span>
                            ) : (
                              <span className="rounded-full border border-emerald-400/30 px-2.5 py-0.5 font-mono text-[8px] tracking-[0.15em] text-emerald-300">SUBSCRIBED</span>
                            )}
                          </Td>
                          <Td>
                            {(m.milestones || []).length > 0 ? (
                              <span className="flex flex-wrap gap-1">
                                {m.milestones.map((label) => (
                                  <span key={label} className="rounded-full border border-cyan-400/40 bg-cyan-400/[0.07] px-2 py-0.5 font-mono text-[8px] tracking-[0.12em] text-cyan-300">
                                    {label}
                                  </span>
                                ))}
                              </span>
                            ) : (
                              <span className="text-zinc-700">-</span>
                            )}
                          </Td>
                          <Td>
                            {m.invite_status === "review" ? (
                              <span className="rounded-full border border-amber-400/40 px-2.5 py-0.5 font-mono text-[8px] tracking-[0.15em] text-amber-300">IN REVIEW</span>
                            ) : m.invite_status === "rejected" ? (
                              <span className="rounded-full border border-red-400/40 px-2.5 py-0.5 font-mono text-[8px] tracking-[0.15em] text-red-300">REJECTED</span>
                            ) : (
                              <span className="rounded-full border border-cyan-400/30 px-2.5 py-0.5 font-mono text-[8px] tracking-[0.15em] text-cyan-400">ACTIVE</span>
                            )}
                          </Td>
                          <Td mono>{fmtDate(m.created_at)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {tab === "activity" && (
                  <table className="w-full" data-testid="admin-activity-table">
                    <thead className="border-b border-white/[0.07] bg-white/[0.02]">
                      <tr>
                        <Th>New Member</Th><Th>Email</Th><Th>Invited By</Th><Th>Invite Code</Th><Th>Status</Th><Th>Action</Th><Th>When</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {activity.map((a, i) => (
                        <tr key={`${a.email}-${i}`} className="border-b border-white/[0.04] last:border-0 hover:bg-white/[0.02]">
                          <Td>{a.name}</Td>
                          <Td mono>{a.email}</Td>
                          <Td>{a.referrer_name}</Td>
                          <Td mono><span className="text-cyan-400">{a.referral}</span></Td>
                          <Td>
                            {a.invite_status === "review" ? (
                              <span className="rounded-full border border-amber-400/40 px-2.5 py-0.5 font-mono text-[8px] tracking-[0.15em] text-amber-300">IN REVIEW</span>
                            ) : a.invite_status === "rejected" ? (
                              <span className="rounded-full border border-red-400/40 px-2.5 py-0.5 font-mono text-[8px] tracking-[0.15em] text-red-300">REJECTED</span>
                            ) : (
                              <span className="rounded-full border border-cyan-400/30 px-2.5 py-0.5 font-mono text-[8px] tracking-[0.15em] text-cyan-400">VERIFIED</span>
                            )}
                          </Td>
                          <Td>
                            {a.invite_status === "review" ? (
                              <span className="flex gap-1.5">
                                <button
                                  data-testid={`approve-invite-${i}`}
                                  onClick={() => reviewInvite(a, "approve")}
                                  className="rounded-full border border-cyan-400/40 bg-cyan-400/[0.08] px-3 py-1 font-mono text-[8px] tracking-[0.15em] text-cyan-300 transition-colors hover:bg-cyan-400/20"
                                >
                                  APPROVE
                                </button>
                                <button
                                  data-testid={`reject-invite-${i}`}
                                  onClick={() => reviewInvite(a, "reject")}
                                  className="rounded-full border border-red-400/40 bg-red-400/[0.06] px-3 py-1 font-mono text-[8px] tracking-[0.15em] text-red-300 transition-colors hover:bg-red-400/15"
                                >
                                  REJECT
                                </button>
                              </span>
                            ) : (
                              <span className="text-zinc-700">-</span>
                            )}
                          </Td>
                          <Td mono>{fmtDate(a.created_at)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {tab === "validators" && (
                  <table className="w-full" data-testid="admin-validators-table">
                    <thead className="border-b border-white/[0.07] bg-white/[0.02]">
                      <tr>
                        <Th>Ref</Th><Th>Name</Th><Th>Email</Th><Th>Telegram / X</Th><Th>Country</Th><Th>Motivation</Th><Th>Applied</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {validators.map((v) => (
                        <tr key={v.application_id} className="border-b border-white/[0.04] last:border-0 hover:bg-white/[0.02]">
                          <Td mono><span className="text-cyan-400">{v.application_id}</span></Td>
                          <Td>{v.full_name}</Td>
                          <Td mono>{v.email}</Td>
                          <Td mono>{v.telegram_x || "-"}</Td>
                          <Td>{v.country}</Td>
                          <Td><span className="block max-w-[280px] truncate text-zinc-500">{v.motivation}</span></Td>
                          <Td mono>{fmtDate(v.created_at)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {tab === "pilots" && (
                  <table className="w-full" data-testid="admin-pilots-table">
                    <thead className="border-b border-white/[0.07] bg-white/[0.02]">
                      <tr>
                        <Th>Ref</Th><Th>Name</Th><Th>Company</Th><Th>Email</Th><Th>Industry</Th><Th>Interest</Th><Th>Requested</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {pilots.map((p) => (
                        <tr key={p.request_id} className="border-b border-white/[0.04] last:border-0 hover:bg-white/[0.02]">
                          <Td mono><span className="text-cyan-400">{p.request_id}</span></Td>
                          <Td>{p.name}</Td>
                          <Td>{p.company}</Td>
                          <Td mono>{p.work_email}</Td>
                          <Td>{p.industry}</Td>
                          <Td><span className="block max-w-[280px] truncate text-zinc-500">{p.interest}</span></Td>
                          <Td mono>{fmtDate(p.created_at)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </main>
    </div>
    </>
  );
}
